import { LRUCache } from 'lru-cache';
import BaseLayer from 'ol/layer/Base';
import ImageLayer from 'ol/layer/Image';
import { ImageStatic } from 'ol/source';
import { revokeBlobUrl, revokeBlobUrlLater } from './blobUrl';

// Two caches with different lifetimes, both bounded by memory (bytes) instead of item count:
//  - playbackCache: frames/tiles preloaded for the time-series animation. They are historical
//    (immutable for a given URL) and are only preloaded once per range/style/products, so they
//    have NO TTL: they live until evicted by size or purged when those conditions change.
//  - navigationCache: real-time and ad-hoc images, short TTL because the same URL may change.

export interface CachedImage {
  url: string; // blob URL
  size: number; // blob size in bytes
  time?: string; // GeoServer time resolved for the image
}

const MB = 1024 * 1024;
// A replay request is one whose TIME is further from now than this
// (same rule as enhancedImageService).
const REAL_TIME_WINDOW_MS = 10 * 60 * 1000;
const NAVIGATION_TTL_MS = 5 * 60 * 1000;

// eslint-disable-next-line no-underscore-dangle
const env = () => (window as any)._env_ ?? {};

const readPositiveInt = (value: string | undefined, fallback: number) => {
  const parsed = parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const createImageCache = ({ maxMB, maxItems, ttl }: {
  maxMB: number; maxItems: number; ttl?: number;
}) => new LRUCache<string, CachedImage>({
  max: maxItems,
  maxSize: maxMB * MB,
  sizeCalculation: (entry) => Math.max(1, entry.size),
  ...(ttl ? { ttl, ttlAutopurge: true } : {}),
  // The blob URL leaves memory together with its cache entry.
  dispose: (entry) => revokeBlobUrlLater(entry.url),
});

export const playbackCache = createImageCache({
  maxMB: readPositiveInt(env().REACT_APP_PLAYBACK_CACHE_MAX_MB, 256),
  // Legacy variable, now an upper bound on entries: DTN tiles are many and small.
  maxItems: readPositiveInt(env().REACT_APP_DTN_MAX_ITEMS_IN_CACHE, 3000),
});

export const navigationCache = createImageCache({
  maxMB: readPositiveInt(env().REACT_APP_NAVIGATION_CACHE_MAX_MB, 64),
  maxItems: readPositiveInt(env().REACT_APP_MAX_ITEMS_IN_CACHE, 5 * 60 + 20),
  ttl: NAVIGATION_TTL_MS,
});

/** True when the TIME param of a GeoServer URL is within the real-time window (or missing). */
export function isRealTimeSrc(src: string): boolean {
  const params = new URLSearchParams(src.split('?')[1] || '');
  const time = params.get('time') || params.get('TIME');
  const timestamp = time ? new Date(time).getTime() : NaN;
  if (Number.isNaN(timestamp)) return true;
  return Math.abs(Date.now() - timestamp) <= REAL_TIME_WINDOW_MS;
}

export const cacheForSrc = (src: string) => (isRealTimeSrc(src) ? navigationCache : playbackCache);

/** Looks the key up in both caches (the classification of a URL can drift over time). */
export const getCachedImage = (key: string): CachedImage | undefined => (
  playbackCache.get(key) ?? navigationCache.get(key)
);

/**
 * Stores an entry. If the cache rejects it (bigger than the whole cache) the blob URL would
 * never be disposed, so it is released right away.
 */
export function storeCachedImage(
  cache: LRUCache<string, CachedImage>,
  key: string,
  entry: CachedImage,
) {
  cache.set(key, entry);
  if (!cache.has(key)) revokeBlobUrl(entry.url);
}

/** Removes every entry (in both caches) whose key matches (contains the text / predicate). */
export function purgeCachedImages(match: string | ((key: string) => boolean)) {
  const matches = typeof match === 'string' ? (key: string) => key.includes(match) : match;
  [playbackCache, navigationCache].forEach((cache) => {
    Array.from(cache.keys()).filter(matches).forEach((key) => cache.delete(key));
  });
}

/** Drops every preloaded frame/tile: use when the preload range changes (a new preload follows). */
export const clearPlaybackCache = () => playbackCache.clear();

/**
 * Drops the cached images of a GeoServer layer (optionally only for one style).
 * Keys are WMS URLs, where LAYERS and STYLES are always followed by another parameter.
 */
export const purgeGeoServerLayerImages = (layers: string, styles?: string) => purgeCachedImages(
  (key) => key.includes(`LAYERS=${layers}&`) && (!styles || key.includes(`STYLES=${styles}&`)),
);

/** Drops the cached tiles of a DTN layer (keys: `<DTN_API>/tiles/<layerId>/<tileSetId>/...`). */
export const purgeDTNLayerImages = (layerId: string) => purgeCachedImages(`/tiles/${layerId}/`);

/** Drops the cached images of whatever product an OpenLayers layer shows. */
export function purgeLayerImages(layer: BaseLayer, styles?: string) {
  if (layer.get('provider') === 'dtn') {
    const layerId = layer.get('internalName') as string | undefined;
    if (layerId) purgeDTNLayerImages(layerId);
    return;
  }
  const source = (layer as ImageLayer<ImageStatic>).getSource();
  const layers = source?.get('layers') as string | undefined;
  if (layers) purgeGeoServerLayerImages(layers, styles);
}

/** Serialized GetFeatureInfo responses (small JSON strings, not images). */
export const featureInfoCache = new LRUCache<string, string>({
  max: 100,
  ttl: NAVIGATION_TTL_MS,
  ttlAutopurge: true,
});
